import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { PanelFooter } from "~/components/panels";
import { EmptyList } from "~/components/empty/empty-list";
import { BaseItem, SortableList } from "~/components/sortable-list";
import { Tabs, TabsContent, TabsList } from "~/components/ui/tabs";
import InputMode from "./input-mode";
import { FinalListTabTrigger } from "./final-list-tab-trigger";
import { KeywordsListSkeleton } from "./keywords-list-skeleton";

type FinalKeywordsTab = "phrases" | "singleWords";

export interface FinalKeywordsViewProps {
  data: {
    phrases: BaseItem[];
    words: BaseItem[];
    phrasesCharacters: number;
    wordsCharacters: number;
  };
  status: { isGenerating: boolean };
  onGenerate: () => Promise<void> | void;
  onChangeOrder: (items: BaseItem[]) => void;
  onChangeWordOrder: (items: BaseItem[]) => void;
  onRemove: (item: BaseItem | undefined) => void | Promise<void>;
  onEdit: (item: BaseItem, value: string) => void;
  onSaveKeyword: (keyword: string) => Promise<void> | void;
  onCancelKeyword?: (keyword: string) => void;
  onCopyPhrases: () => Promise<void> | void;
  onCopyWords: () => Promise<void> | void;
}

export const FinalKeywordsView = ({
  data,
  status,
  onGenerate,
  onChangeOrder,
  onChangeWordOrder,
  onRemove,
  onEdit,
  onSaveKeyword,
  onCancelKeyword,
  onCopyPhrases,
  onCopyWords,
}: FinalKeywordsViewProps) => {
  const { t } = useTranslation(["components"]);
  const { phrases, words, phrasesCharacters, wordsCharacters } = data;
  const { isGenerating } = status;

  const [activeTab, setActiveTab] = useState<FinalKeywordsTab>("phrases");
  const [inputMode, setInputMode] = useState(false);

  const hasKeywords = phrases.length > 0;
  const hasFinalWords = words.length > 0;
  const isPhrases = activeTab === "phrases";

  const handleSave = async (keyword: string) => {
    await onSaveKeyword(keyword);
    setInputMode(false);
  };

  const renderFooter = (
    <PanelFooter>
      <Button
        size="small"
        variant="tertiary"
        icon="copy"
        label={
          isPhrases
            ? t("components:keywordsTab.finalKeywords.actions.copyPhrases")
            : t("components:keywordsTab.finalKeywords.actions.copyWords")
        }
        disabled={
          isPhrases
            ? !hasKeywords || isGenerating
            : !hasFinalWords || isGenerating
        }
        onClick={isPhrases ? onCopyPhrases : onCopyWords}
      />
    </PanelFooter>
  );

  const showEmptyState =
    !hasKeywords && !inputMode && isPhrases && !isGenerating;

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as FinalKeywordsTab)}
      className="flex flex-col h-full w-100 shrink-0 gap-0"
    >
      <div className="bg-primary-50 shrink-0 rounded-t-xl">
        <TabsList className="bg-transparent w-full justify-start h-auto group-data-[orientation=horizontal]/tabs:h-auto p-0 m-0">
          <FinalListTabTrigger
            value="phrases"
            testId="phrases-tab"
            onAdd={() => {
              setInputMode(true);
              setActiveTab("phrases");
            }}
            addDisabled={inputMode || isGenerating}
            characterCount={phrasesCharacters}
          />

          <FinalListTabTrigger
            value="singleWords"
            testId="single-words-tab"
            disabled={!hasFinalWords}
            characterCount={wordsCharacters}
          />
        </TabsList>
      </div>

      <div className="flex flex-col flex-1 border-x border-b border-t-0 border-primary-200 rounded-b-xl overflow-hidden bg-white">
        <div className="flex-1 overflow-hidden">
          {showEmptyState ? (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <EmptyList
                title={t("components:keywordsTab.finalKeywords.empty.header")}
                text={t(
                  "components:keywordsTab.finalKeywords.empty.description",
                )}
                buttons={[
                  <Button
                    key="generate"
                    label={t(
                      "components:keywordsTab.finalKeywords.empty.generate",
                    )}
                    onClick={onGenerate}
                    disabled={isGenerating}
                    icon={isGenerating ? "loader" : "sparkles"}
                    iconClassName={isGenerating ? "animate-spin" : undefined}
                    size="small"
                    analyticsId="button_generate_and_autofill_keywords"
                  />,
                ]}
                classes={{
                  container: "flex flex-col items-center justify-center h-full",
                  text: "text-sm m-0 pb-4",
                }}
                image
              />
            </div>
          ) : (
            <>
              <TabsContent
                value="phrases"
                className="h-full mt-0 overflow-y-auto"
              >
                {isGenerating ? (
                  <KeywordsListSkeleton count={phrases.length} />
                ) : (
                  <>
                    <SortableList
                      items={phrases}
                      onChangeOrder={onChangeOrder}
                      onRemove={onRemove}
                      onEdit={onEdit}
                      disabled={isGenerating}
                    />
                    {inputMode && (
                      <InputMode
                        onSave={handleSave}
                        onCancel={(keyword) => {
                          onCancelKeyword?.(keyword);
                          setInputMode(false);
                        }}
                      />
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent
                value="singleWords"
                className="h-full mt-0 overflow-y-auto"
              >
                {isGenerating ? (
                  <KeywordsListSkeleton count={words.length} />
                ) : (
                  <SortableList
                    items={words}
                    onChangeOrder={onChangeWordOrder}
                    disabled={isGenerating}
                  />
                )}
              </TabsContent>
            </>
          )}
        </div>

        {renderFooter}
      </div>
    </Tabs>
  );
};
